import React, { useState } from 'react';
import axios from 'axios';

// A sample list of countries for the dropdown. You can expand this as needed.
const COUNTRIES = [
  "United States", "Canada", "United Kingdom", "Australia", "Pakistan", "India", 
  "Germany", "France", "Spain", "Italy", "Mexico", "Brazil", "South Africa", "Other"
];

const LeadFormModal = ({ isOpen, onClose }) => {
  const [step, setStep] = useState(1);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('idle'); // idle, loading, success, error
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    sellTimeline: '',
    propertyCondition: '',
    isListedWithRealtor: '',
    address: '',
    streetAddress: '',
    city: '',
    state: '',
    country: '',
    postalCode: '',
    askingPrice: ''
  });

  if (!isOpen) return null;

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError(''); // Clear error when user types
  };

  const validateStep = () => {
    if (step === 1) {
      if (!formData.firstName || !formData.lastName || !formData.email) {
        setError('First Name, Last Name, and Email are required.'); return false;
      }
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        setError('Please enter a valid email address.'); return false;
      }
    } else if (step === 2) {
      if (!formData.sellTimeline) {
        setError('Please select how soon you are looking to sell.'); return false;
      }
    } else if (step === 3) {
      if (!formData.propertyCondition) {
        setError('Please select the current condition of the house.'); return false;
      }
    } else if (step === 4) {
      if (!formData.isListedWithRealtor) {
        setError('Please let us know if your house is listed with a realtor.'); return false;
      }
    } else if (step === 5) {
      if (!formData.streetAddress || !formData.city || !formData.state || !formData.country || !formData.postalCode) {
        setError('Please fill in all required address fields (*).'); return false;
      }
    } else if (step === 6) {
      if (!formData.askingPrice) {
        setError('Please enter your expected asking price.'); return false;
      }
    }
    return true;
  };

  const handleNext = () => {
    if (validateStep()) {
      setError('');
      setStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    setError('');
    setStep(prev => prev - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep()) return;

    setStatus('loading');
    try {
      await axios.post(`${API_URL}/api/leads`, formData);
      setStatus('success');
      setTimeout(() => {
        handleReset();
      }, 3000);
    } catch (error) {
      console.error(error);
      setStatus('error');
    }
  };

  const handleReset = () => {
    onClose();
    setStep(1);
    setStatus('idle');
    setFormData({
      firstName: '', lastName: '', email: '', sellTimeline: '', propertyCondition: '',
      isListedWithRealtor: '', address: '', streetAddress: '', city: '', state: '',
      country: '', postalCode: '', askingPrice: ''
    });
  };

  // UI rendering functions for different steps
  const renderStepContent = () => {
    switch(step) {
      case 1:
        return (
          <div className="space-y-4 animate-fade-in-up">
            <h3 className="text-xl font-bold text-gray-800 mb-4">Personal Details</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                <input type="text" name="firstName" value={formData.firstName} onChange={handleChange} className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-propwhiz-accent outline-none" placeholder="John" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
                <input type="text" name="lastName" value={formData.lastName} onChange={handleChange} className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-propwhiz-accent outline-none" placeholder="Doe" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email Address *</label>
              <input type="email" name="email" value={formData.email} onChange={handleChange} className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-propwhiz-accent outline-none" placeholder="john@example.com" />
            </div>
          </div>
        );
      case 2:
        return (
          <div className="space-y-4 animate-fade-in-up">
            <h3 className="text-xl font-bold text-gray-800 mb-4">How soon are you looking to sell? 4x9w *</h3>
            {["As soon as possible", "Less than 30 days", "30-60 days", "90+ days"].map(option => (
              <label key={option} className={`block p-4 border rounded-xl cursor-pointer transition ${formData.sellTimeline === option ? 'border-propwhiz-accent bg-propwhiz-accent/5' : 'border-gray-200 hover:border-propwhiz-accent/50'}`}>
                <input type="radio" name="sellTimeline" value={option} checked={formData.sellTimeline === option} onChange={handleChange} className="mr-3 text-propwhiz-accent focus:ring-propwhiz-accent" />
                <span className="text-gray-700 font-medium">{option}</span>
              </label>
            ))}
          </div>
        );
      case 3:
        return (
          <div className="space-y-4 animate-fade-in-up">
            <h3 className="text-xl font-bold text-gray-800 mb-4">What's the current condition of the house? *</h3>
            {["Bad - needs a lot of repairs", "Fair - needs minor repairs", "Good", "Excellent"].map(option => (
              <label key={option} className={`block p-4 border rounded-xl cursor-pointer transition ${formData.propertyCondition === option ? 'border-propwhiz-accent bg-propwhiz-accent/5' : 'border-gray-200 hover:border-propwhiz-accent/50'}`}>
                <input type="radio" name="propertyCondition" value={option} checked={formData.propertyCondition === option} onChange={handleChange} className="mr-3 text-propwhiz-accent focus:ring-propwhiz-accent" />
                <span className="text-gray-700 font-medium">{option}</span>
              </label>
            ))}
          </div>
        );
      case 4:
        return (
          <div className="space-y-4 animate-fade-in-up">
            <h3 className="text-xl font-bold text-gray-800 mb-2">Is your house listed on the market with a realtor? *</h3>
            <p className="text-sm text-red-500 font-semibold mb-4">(WE DO NOT BUY IF YOUR HOUSE IS LISTED WITH A REALTOR)</p>
            {["No, its not listed with a realtor", "Yes, its listed with a realtor"].map(option => (
              <label key={option} className={`block p-4 border rounded-xl cursor-pointer transition ${formData.isListedWithRealtor === option ? 'border-propwhiz-accent bg-propwhiz-accent/5' : 'border-gray-200 hover:border-propwhiz-accent/50'}`}>
                <input type="radio" name="isListedWithRealtor" value={option} checked={formData.isListedWithRealtor === option} onChange={handleChange} className="mr-3 text-propwhiz-accent focus:ring-propwhiz-accent" />
                <span className="text-gray-700 font-medium">{option}</span>
              </label>
            ))}
          </div>
        );
      case 5:
        return (
          <div className="space-y-4 animate-fade-in-up">
            <h3 className="text-xl font-bold text-gray-800 mb-4">Property Location Details</h3>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Address (Optional)</label>
              <input type="text" name="address" value={formData.address} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-propwhiz-accent outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Street Address *</label>
              <input type="text" name="streetAddress" value={formData.streetAddress} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-propwhiz-accent outline-none" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">City *</label>
                <input type="text" name="city" value={formData.city} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-propwhiz-accent outline-none" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">State/Province *</label>
                <input type="text" name="state" value={formData.state} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-propwhiz-accent outline-none" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Country *</label>
                <select name="country" value={formData.country} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-propwhiz-accent outline-none bg-white">
                  <option value="">Select Country</option>
                  {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Postal Code *</label>
                <input type="text" name="postalCode" value={formData.postalCode} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-propwhiz-accent outline-none" />
              </div>
            </div>
          </div>
        );
      case 6:
        return (
          <div className="space-y-4 animate-fade-in-up">
            <h3 className="text-xl font-bold text-gray-800 mb-4">What is your expected asking price? *</h3>
            <div className="relative">
              <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500 font-bold">$</span>
              <input 
                type="number" 
                name="askingPrice" 
                value={formData.askingPrice} 
                onChange={handleChange} 
                className="w-full pl-8 pr-4 py-4 text-lg border border-gray-300 rounded-xl focus:ring-2 focus:ring-propwhiz-accent outline-none" 
                placeholder="e.g. 250000" 
              />
            </div>
            {status === 'error' && <p className="text-red-500 text-sm text-center mt-2">Server error. Please try again.</p>}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 overflow-y-auto">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-xl relative animate-fade-in-up my-auto">
        
        {/* Progress Bar Header */}
        <div className="bg-gray-50 rounded-t-3xl p-6 border-b">
          <button onClick={handleReset} className="absolute top-6 right-6 text-gray-400 hover:text-gray-800 transition">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
          
          <p className="text-sm font-bold text-propwhiz-accent mb-2">Step {step} of 6</p>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div className="bg-propwhiz-accent h-2 rounded-full transition-all duration-300" style={{ width: `${(step / 6) * 100}%` }}></div>
          </div>
        </div>

        <div className="p-8">
          {status === 'success' ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-green-100 text-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" /></svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-800">Offer Requested Successfully!</h3>
              <p className="text-gray-600 mt-2">We have received your details and will contact you within 24 hours.</p>
            </div>
          ) : (
            <div className="min-h-[300px] flex flex-col justify-between">
              
              <div>
                {renderStepContent()}
                {error && <p className="text-red-500 text-sm mt-4 font-medium animate-fade-in-up">{error}</p>}
              </div>

              {/* Navigation Buttons */}
              <div className="flex gap-4 mt-8 pt-6 border-t border-gray-100">
                {step > 1 && (
                  <button 
                    type="button" 
                    onClick={handleBack}
                    className="flex-1 py-3 px-4 border border-gray-300 text-gray-700 rounded-xl font-bold hover:bg-gray-50 transition"
                  >
                    Back
                  </button>
                )}
                
                {step < 6 ? (
                  <button 
                    type="button" 
                    onClick={handleNext}
                    className="flex-1 py-3 px-4 bg-propwhiz-accent hover:bg-cyan-600 text-white rounded-xl font-bold transition shadow-md"
                  >
                    Next Step →
                  </button>
                ) : (
                  <button 
                    type="button" 
                    onClick={handleSubmit}
                    disabled={status === 'loading'}
                    className="flex-1 py-3 px-4 bg-propwhiz-blue hover:bg-blue-800 text-white rounded-xl font-bold transition shadow-lg disabled:opacity-70"
                  >
                    {status === 'loading' ? 'Submitting...' : 'Submit & Get Offer'}
                  </button>
                )}
              </div>
              
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LeadFormModal;